import { useEffect, useState } from 'react';
import { useLicenseModels } from './useLicenseModels';
import {
	UserLicenseDescriptions,
	UserLicenseLabels,
	UserLicenseModel
} from 'store/account/subscription';
import { BillingPeriodUnit, LedidiPlanCurrency } from 'store/account/payments';
import { useSubscription, useTranslation } from 'hooks/store';
import { GenericMap } from 'types';
import { usePrevious } from 'hooks/utils';

export interface ChangePlanLicenseModel {
	id: string;
	licenseModel: UserLicenseModel;
	count: number;
	minimumCount: number;
	unitPrice: number;
	label: string;
	description: string;
}

export function useChangePlanLicenseQuantities(
	currency: LedidiPlanCurrency,
	billingUnit: BillingPeriodUnit
) {
	const [
		{
			data: { licenseModelsMetadataById, licenseQuantitiesByName }
		}
	] = useLicenseModels();

	const [
		{
			data: {
				subscriptionTypes: { isLedidiCollaborator }
			}
		}
	] = useSubscription();

	const { translate } = useTranslation();

	const [changePlanLicenseModels, setChangePlanLicenseModels] =
		useState<GenericMap<ChangePlanLicenseModel>>();

	useEffect(() => {
		if (licenseModelsMetadataById) {
			let defaultChangePlanLicenseModels = {};
			Object.keys(licenseModelsMetadataById).forEach(licenseKey => {
				if (
					licenseModelsMetadataById[licenseKey].billingUnit === billingUnit &&
					licenseModelsMetadataById[licenseKey].currency === currency
				) {
					const licenseModel = licenseModelsMetadataById[licenseKey].licenseModel;
					defaultChangePlanLicenseModels = {
						...defaultChangePlanLicenseModels,
						[licenseKey]: {
							id: licenseKey,
							licenseModel: licenseModel,
							label:
								translate(
									() => UserLicenseLabels[licenseModel as UserLicenseModel]
								) ?? licenseModel,
							description:
								translate(
									() => UserLicenseDescriptions[licenseModel as UserLicenseModel]
								) ?? licenseModel,
							count:
								licenseQuantitiesByName && licenseQuantitiesByName[licenseModel]
									? licenseQuantitiesByName[licenseModel]
									: licenseModel === UserLicenseModel.Full ||
									  (licenseModel === UserLicenseModel.Collaborator &&
											isLedidiCollaborator)
									? 1
									: 0,
							minimumCount:
								licenseModel === UserLicenseModel.Full ||
								(licenseModel === UserLicenseModel.Collaborator &&
									isLedidiCollaborator)
									? 1
									: 0,
							unitPrice: licenseModelsMetadataById[licenseKey].price
						}
					};
				}
			});
			setChangePlanLicenseModels(defaultChangePlanLicenseModels);
		}
	}, []);

	function changeCount(licenseId: string, count: number) {
		setChangePlanLicenseModels(prev => {
			if (prev && prev[licenseId]) {
				return {
					...prev,
					[licenseId]: {
						...prev[licenseId],
						count: count >= prev[licenseId].minimumCount ? count : 0
					}
				};
			} else return prev;
		});
	}

	function getLicenseModelId(
		currency: LedidiPlanCurrency,
		billingUnit: BillingPeriodUnit,
		licenseModel: UserLicenseModel
	) {
		let foundKey = null;
		Object.keys(licenseModelsMetadataById).forEach(licenseKey => {
			if (
				licenseModelsMetadataById[licenseKey].billingUnit === billingUnit &&
				licenseModelsMetadataById[licenseKey].currency === currency &&
				licenseModelsMetadataById[licenseKey].licenseModel === licenseModel
			)
				foundKey = licenseKey;
		});
		return foundKey;
	}

	useEffect(() => {
		// change id and price
	}, [currency]);

	const prevBillingUnit = usePrevious(billingUnit);
	useEffect(() => {
		// change id and price
		if (
			prevBillingUnit &&
			billingUnit !== prevBillingUnit &&
			licenseModelsMetadataById &&
			changePlanLicenseModels
		) {
			let newChangePlanLicenseModels = {};
			Object.keys(licenseModelsMetadataById).forEach(licenseKey => {
				if (
					licenseModelsMetadataById[licenseKey].billingUnit === billingUnit &&
					licenseModelsMetadataById[licenseKey].currency === currency
				) {
					const licenseModel = licenseModelsMetadataById[licenseKey].licenseModel;
					const prevLicenseModelId = getLicenseModelId(
						currency,
						prevBillingUnit,
						licenseModel
					);

					if (prevLicenseModelId)
						newChangePlanLicenseModels = {
							...newChangePlanLicenseModels,
							[licenseKey]: {
								...changePlanLicenseModels[prevLicenseModelId],
								id: licenseKey,
								unitPrice: licenseModelsMetadataById[licenseKey].price
							}
						};
				}
			});
			setChangePlanLicenseModels(newChangePlanLicenseModels);
		}
	}, [billingUnit, changePlanLicenseModels]);

	return {
		changePlanLicenseModels: changePlanLicenseModels
			? Object.values(changePlanLicenseModels)
			: [],
		changeCount
	};
}
